import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { AuthError } from "@/services/api";
import LoadingSpinner from "@/components/LoadingSpinner";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

function SentryFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <h1 className="text-xl font-medium text-foreground">Something went wrong</h1>
        <p className="text-sm text-muted-foreground">An unexpected error occurred.</p>
        <button
          onClick={() => window.location.assign('/')}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm"
        >
          Reload
        </button>
      </div>
    </div>
  );
}

// Pages
import Auth from "./pages/Auth";
import ProjectsList from "./pages/admin/ProjectsList";
import NewProject from "./pages/admin/NewProject";
import EditProject from "./pages/admin/EditProject";
import ProjectReport from "./pages/admin/ProjectReport";
import ProjectExports from "./pages/admin/ProjectExports";
import ProjectSubscriptions from "./pages/admin/ProjectSubscriptions";
import TesterDashboard from "./pages/tester/TesterDashboard";
import Reporting from "./pages/admin/Reporting";
import SharePoint from "./pages/SharePoint";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof AuthError) return false;
        return failureCount < 1;
      },
    },
  },
});

// Protected route wrapper
function ProtectedRoute({ children, requiredRole }: { children: React.ReactNode; requiredRole?: 'admin' | 'tester' }) {
  const { user, isAdmin, isTester, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (requiredRole === 'admin' && !isAdmin) {
    return <Navigate to="/tester/dashboard" replace />;
  }

  if (requiredRole === 'tester' && !isTester && !isAdmin) {
    return <Navigate to="/admin/projects" replace />;
  }

  return <>{children}</>;
}

// Root redirect with loading guard
function RootRedirect() {
  const { user, isAdmin, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <Navigate to={isAdmin ? "/admin/projects" : "/tester/dashboard"} replace />;
}

function AppRoutes() {
  return (
    <SentryRoutes>
      {/* Redirect root based on auth status */}
      <Route path="/" element={<RootRedirect />} />

      {/* Auth */}
      <Route path="/auth" element={<Auth />} />

      {/* Admin Routes */}
      <Route
        path="/admin/projects"
        element={
          <ProtectedRoute requiredRole="admin">
            <ProjectsList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/projects/new"
        element={
          <ProtectedRoute requiredRole="admin">
            <NewProject />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/projects/:id/edit"
        element={
          <ProtectedRoute requiredRole="admin">
            <EditProject />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/projects/:id/report"
        element={
          <ProtectedRoute requiredRole="admin">
            <ProjectReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/projects/:id/exports"
        element={
          <ProtectedRoute requiredRole="admin">
            <ProjectExports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/projects/:id/subscriptions"
        element={
          <ProtectedRoute requiredRole="admin">
            <ProjectSubscriptions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reporting"
        element={
          <ProtectedRoute requiredRole="admin">
            <Reporting />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/sharepoint"
        element={
          <ProtectedRoute requiredRole="admin">
            <SharePoint />
          </ProtectedRoute>
        }
      />

      {/* Tester Routes */}
      <Route
        path="/tester/dashboard"
        element={
          <ProtectedRoute requiredRole="tester">
            <TesterDashboard />
          </ProtectedRoute>
        }
      />

      {/* Legacy redirects */}
      <Route path="/dashboard" element={<Navigate to="/" replace />} />
      <Route path="/admin" element={<Navigate to="/admin/projects" replace />} />
      <Route path="/testing" element={<Navigate to="/tester/dashboard" replace />} />
      <Route path="/reporting" element={<Navigate to="/admin/reporting" replace />} />

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </SentryRoutes>
  );
}

const App = () => (
  <Sentry.ErrorBoundary fallback={<SentryFallback />}>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
